<template>
  <div
    class="h-screen bg-white"
    @mouseleave="mouseLeaveAssessmentsWindow"
  >
    <div
      v-if="invitationError"
      class="p-6"
      role="alert"
    >
      <p>{{ invitationError }}</p>
      <BaseButton
        class="mt-4"
        @click="validateToken"
      >
        Try again
      </BaseButton>
    </div>
    <div v-else-if="invitationReady && exam">
      <AssessmentInstructions
        v-if="!assessmentStarted"
        :exam="exam"
        :timer-disabled="timerDisabled"
        @begin-test="startExam($event)"
      />
      <AssessmentContainer
        v-else
        :organisation-id="organisationId"
        :timer-disabled="timerDisabled"
        :exam="exam"
        :anti-cheat-data="antiCheatData"
        @completed="assessmentCompleted = true"
      />
    </div>
    <div
      v-else
      class="flex-grow my-12"
    >
      <Loader />
    </div>
  </div>
</template>

<script>
import AssessmentContainer from '@components/Assessments/AssessmentContainer'
import AssessmentInstructions from '@components/Assessments/AssessmentInstructions'
import Loader from '@components/Loader'

import { mapGetters } from 'vuex'
import { antiCheating } from '@utils/mixins/antiCheating'

export default {
  components: {
    AssessmentContainer,
    AssessmentInstructions,
    Loader
  },

  mixins: [antiCheating],

  data() {
    return {
      assessmentStarted: false,
      assessmentCompleted: false,
      invitationReady: false,
      invitationError: '',
      timerDisabled: false
    }
  },

  computed: {
    ...mapGetters({
      haveExamsLoaded: 'exams/haveExamsLoaded',
      examBySlug: 'exams/examBySlug',
      isTokenValid: 'candidates/isTokenValid',
      organisationPreventWebcamRequest: 'candidates/organisationPreventWebcamRequest',
      organisationUsesUnlimitedTimeExams: 'candidates/organisationUsesUnlimitedTimeExams'
    }),

    /**
     * @return {String}
     */
    examSlug() {
      return this.$route.params.assessment
    },

    /**
     * @return {string}
     */
    candidateId() {
      return this.$route.query.candidate
    },

    /**
     * @return {string}
     */
    jobId() {
      return this.$route.query.job
    },

    /**
     * @return {string}
     */
    sittingToken() {
      return this.$route.query.sitting
    },

    /**
     * @return {string}
     */
    token() {
      return this.$route.query.token
    },

    /**
     * To be passed to the next part of the journey
     *
     * @return {Object}
     */
    query() {
      return {
        candidate: this.candidateId,
        job: this.jobId,
        sitting: this.sittingToken,
        token: this.token
      }
    },

    /**
     * @return {Object}
     */
    exam() {
      return this.examBySlug(this.examSlug)
    },

    /**
     * @return {String}
     */
    organisationId() {
      return this.exam.organisationId
    }
  },

  created() { this.validateToken() },

  methods: {
    /**
     * @param {Boolean} toggle
     */
    startExam(options) {
      this.assessmentStarted = true
      if (options && options.fullScreen) { this.requestFullScreenAssessment() }
    },

    async validateToken() {
      this.invitationReady = false
      this.invitationError = ''
      if (!this.candidateId || !this.token) {
        this.invitationError = 'This invitation is incomplete. Open the full link from your invitation or contact the organiser.'
        return
      }
      try {
        const candidate = await this.$store.dispatch('candidates/validateToken', {
          candidateId: this.candidateId, jobId: this.jobId, sittingToken: this.sittingToken, token: this.token
        })
        if (!candidate) { throw new Error('Invalid invitation') }
        this.timerDisabled = candidate.organisationUsesUnlimitedTimeExams
        await this.$store.dispatch('exams/getExams', this.token)
        if (!this.exam) { throw new Error('Assessment unavailable') }
        this.invitationReady = true
      } catch (error) {
        const status = error.response && error.response.status
        this.invitationError = status && status < 500 ? 'This invitation could not be verified. Open your latest invitation or contact the organiser.'
          : 'We could not load your invitation. Check your connection and try again.'
      }
    }
  }
}
</script>
